import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Plus, Loader2, CheckCircle2, ArrowRightLeft, TrendingUp, Ban } from "lucide-react";
import { api } from "@/lib/api";
import { usePermissions } from "@/lib/permissions";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { TabFilter } from "@/components/TabFilter";
import { TableToolbar } from "@/components/TableToolbar";
import { DetailDrawer, DrawerSection, DrawerField } from "@/components/DetailDrawer";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { fmtNum, partyLabel } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";
import { CompanyField } from "@/components/CompanyField";

const CATEGORIES = ["PLANT", "VEHICLE", "EQUIPMENT", "FURNITURE", "IT", "BUILDING"];
const METHODS = ["SLM", "WDV"];

const ACQ_LABEL = {
  FROM_BILL: "From purchase bill", PO_CAPITAL: "From PO (asset line)", OPENING: "Already owned (opening)",
  BANK: "Paid from bank", CREDIT: "Bought on credit",
};

export default function AssetRegister() {
  const { can } = usePermissions();
  const canWrite = can("ASSETS_PLANT", "CREATE");
  const { companies } = useCompany() || {};
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("ALL");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/assets");
      setAssets(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const tabs = [
    { key: "ALL",      label: "All Assets", count: assets.length , sub: "₹" + assets.reduce((a, x) => a + Number(x.book_value || 0), 0).toLocaleString("en-IN", { maximumFractionDigits: 0 }) + " book value" },
    { key: "DRAFT",    label: "Draft",      count: assets.filter(a => a.status === "DRAFT").length },
    { key: "ACTIVE",   label: "Active",     count: assets.filter(a => a.status === "ACTIVE").length },
    { key: "DISPOSED", label: "Disposed",   count: assets.filter(a => a.status === "DISPOSED").length },
  ];

  const shown = useMemo(() => {
    let list = assets;
    if (activeTab !== "ALL") list = list.filter(a => a.status === activeTab);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(a =>
        (a.code || "").toLowerCase().includes(q) ||
        (a.name || "").toLowerCase().includes(q) ||
        (a.category || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [assets, activeTab, search]);

  const totalCost = assets.reduce((s, a) => s + Number(a.cost || 0), 0);
  const totalBookValue = assets.reduce((s, a) => s + Number(a.book_value || 0), 0);


  return (
    <div className="space-y-6" data-testid="asset-register-page">
      <PageHeader
        title="Asset Register"
        description="Fixed assets with cost, accumulated depreciation and book value. Draft assets (from a capitalised bill line) need a method and confirmation before they start depreciating."
        action={canWrite && <CreateAssetDialog companies={companies} onCreated={load} />}
      />
      <TabFilter tabs={tabs} active={activeTab} onChange={setActiveTab} />
      <TableToolbar
        search={search}
        onSearch={setSearch}
        csvData={shown.map(a => ({
          Code: a.code, Name: a.name, Category: a.category,
          Cost: a.cost, "Book Value": a.book_value, Status: a.status,
        }))}
        csvFilename="assets.csv"
      />
      <div className="rounded-xl border border-border bg-card">
        {!loading && assets.length === 0 ? (
          <EmptyState title="No assets yet" description="Create one, or capitalise a purchase bill line from Purchase Bills." testId="asset-register-empty-state" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Category</TableHead><TableHead className="text-right">Cost</TableHead><TableHead className="text-right">Accum. dep.</TableHead><TableHead className="text-right">Book value</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((a) => (
                <TableRow key={a.id} data-testid={`asset-row-${a.code}`}>
                  <TableCell className="font-mono text-xs text-foreground">{a.code}</TableCell>
                  <TableCell className="text-foreground">
                    {a.name}
                    {a.acquisition_mode && <div className="text-[10px] text-muted-foreground">{ACQ_LABEL[a.acquisition_mode] || a.acquisition_mode}</div>}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{a.category}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-foreground">{fmtNum(a.cost, 2)}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-muted-foreground">{fmtNum(a.accumulated_depreciation, 2)}</TableCell>
                  <TableCell className="text-right font-mono text-xs font-semibold text-foreground">{fmtNum(a.book_value, 2)}</TableCell>
                  <TableCell><StatusPill value={a.status} /></TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1.5">
                      <button
                        type="button"
                        onClick={() => setSelected(a)}
                        className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                        title="View"
                        data-testid={`view-asset-${a.code}`}
                      >
                        <TrendingUp className="h-3.5 w-3.5" />
                      </button>
                      {canWrite && a.status === "DRAFT" && <ConfirmAssetDialog asset={a} onDone={load} />}
                      {canWrite && a.status === "ACTIVE" && <AssetActionsDialog asset={a} companies={companies} onDone={load} />}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <DetailDrawer
        open={!!selected}
        onClose={() => setSelected(null)}
        title={selected?.name}
        subtitle={`${selected?.category} · ${selected?.code}`}
      >
        <DrawerSection label="Asset Details">
          <DrawerField label="Code" value={selected?.code} mono />
          <DrawerField label="Name" value={selected?.name} />
          <DrawerField label="Category" value={selected?.category} />
          <DrawerField label="Status" value={selected?.status} />
          <DrawerField label="Acquisition Mode" value={selected?.acquisition_mode ? ACQ_LABEL[selected.acquisition_mode] || selected.acquisition_mode : null} />
        </DrawerSection>
        <DrawerSection label="Valuation">
          <DrawerField label="Cost" value={selected?.cost ? fmtNum(selected.cost, 2) : null} mono />
          <DrawerField label="Accum. Depreciation" value={selected?.accumulated_depreciation ? fmtNum(selected.accumulated_depreciation, 2) : null} mono />
          <DrawerField label="Book Value" value={selected?.book_value ? fmtNum(selected.book_value, 2) : null} mono />
          <DrawerField label="Dep. Method" value={selected?.dep_method} />
          <DrawerField label="Dep. Rate" value={selected?.dep_rate ? `${selected.dep_rate}%` : null} />
          <DrawerField label="Useful Life" value={selected?.useful_life_years ? `${selected.useful_life_years} yrs` : null} />
        </DrawerSection>
      </DetailDrawer>
    </div>
  );
}

function AssetActionsDialog({ asset, onDone }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("move");
  const [move, setMove] = useState({ to_location: "", moved_date: "", reason: "" });
  const [reval, setReval] = useState({ new_value: "", revaluation_date: "", reason: "" });
  const [disposal, setDisposal] = useState({ sale_price: "", disposal_date: "" });
  const [saving, setSaving] = useState(false);

  const submitMove = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/assets/${asset.id}/movements`, { ...move, to_location: move.to_location || null });
      toast.success("Asset moved");
      setOpen(false);
      onDone?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const submitReval = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/assets/${asset.id}/revalue`, reval);
      toast.success("Revaluation posted to Revaluation Reserve");
      setOpen(false);
      onDone?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const submitDisposal = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post(`/assets/${asset.id}/dispose`, disposal);
      toast.success(`Disposed — ${Number(data.gain_loss) >= 0 ? "gain" : "loss"} of ${fmtNum(Math.abs(data.gain_loss), 2)}`);
      setOpen(false);
      onDone?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" data-testid={`asset-actions-button-${asset.code}`}><ArrowRightLeft className="h-3.5 w-3.5" /> Manage</Button></DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid={`asset-actions-dialog-${asset.code}`}>
        <DialogHeader><DialogTitle>{asset.code} — {asset.name}</DialogTitle></DialogHeader>
        <div className="flex gap-1.5 border-b border-border pb-2">
          <Button size="sm" variant={tab === "move" ? "default" : "ghost"} onClick={() => setTab("move")} data-testid="asset-tab-move"><ArrowRightLeft className="h-3.5 w-3.5" /> Move</Button>
          <Button size="sm" variant={tab === "reval" ? "default" : "ghost"} onClick={() => setTab("reval")} data-testid="asset-tab-revalue"><TrendingUp className="h-3.5 w-3.5" /> Revalue</Button>
          <Button size="sm" variant={tab === "dispose" ? "default" : "ghost"} onClick={() => setTab("dispose")} data-testid="asset-tab-dispose"><Ban className="h-3.5 w-3.5" /> Dispose</Button>
        </div>
        {tab === "move" && (
          <form onSubmit={submitMove} className="space-y-3" data-testid="asset-move-form">
            <div><Label>New location</Label><Input value={move.to_location} onChange={(e) => setMove({ ...move, to_location: e.target.value })} className="mt-1.5" data-testid="asset-move-location-input" /></div>
            <div><Label>Date</Label><Input required type="date" value={move.moved_date} onChange={(e) => setMove({ ...move, moved_date: e.target.value })} className="mt-1.5" data-testid="asset-move-date-input" /></div>
            <div><Label>Reason</Label><Input required value={move.reason} onChange={(e) => setMove({ ...move, reason: e.target.value })} className="mt-1.5" data-testid="asset-move-reason-input" /></div>
            <DialogFooter><Button type="submit" disabled={saving} data-testid="asset-move-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Move</Button></DialogFooter>
          </form>
        )}
        {tab === "reval" && (
          <form onSubmit={submitReval} className="space-y-3" data-testid="asset-revalue-form">
            <p className="text-xs text-muted-foreground">Current book value: <span className="font-mono font-semibold text-foreground">{fmtNum(asset.book_value, 2)}</span></p>
            <div><Label>New value</Label><Input required type="number" step="0.01" value={reval.new_value} onChange={(e) => setReval({ ...reval, new_value: e.target.value })} className="mt-1.5" data-testid="asset-revalue-new-value-input" /></div>
            <div><Label>Date</Label><Input required type="date" value={reval.revaluation_date} onChange={(e) => setReval({ ...reval, revaluation_date: e.target.value })} className="mt-1.5" data-testid="asset-revalue-date-input" /></div>
            <div><Label>Reason</Label><Input value={reval.reason} onChange={(e) => setReval({ ...reval, reason: e.target.value })} className="mt-1.5" data-testid="asset-revalue-reason-input" /></div>
            <DialogFooter><Button type="submit" disabled={saving} data-testid="asset-revalue-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Revalue</Button></DialogFooter>
          </form>
        )}
        {tab === "dispose" && (
          <form onSubmit={submitDisposal} className="space-y-3" data-testid="asset-dispose-form">
            <p className="text-xs text-muted-foreground">Book value at disposal: <span className="font-mono font-semibold text-foreground">{fmtNum(asset.book_value, 2)}</span></p>
            <div><Label>Sale price</Label><Input required type="number" step="0.01" value={disposal.sale_price} onChange={(e) => setDisposal({ ...disposal, sale_price: e.target.value })} className="mt-1.5" data-testid="asset-dispose-sale-price-input" /></div>
            <div><Label>Date</Label><Input required type="date" value={disposal.disposal_date} onChange={(e) => setDisposal({ ...disposal, disposal_date: e.target.value })} className="mt-1.5" data-testid="asset-dispose-date-input" /></div>
            <DialogFooter><Button type="submit" variant="destructive" disabled={saving} data-testid="asset-dispose-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Dispose</Button></DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CreateAssetDialog({ companies, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    code: "", name: "", company_id: "", category: "EQUIPMENT", location: "", purchase_date: "",
    cost: "", salvage_value: "0", method: "SLM", useful_life_years: "", rate_percent: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/assets", {
        ...form,
        useful_life_years: form.method === "SLM" ? form.useful_life_years : null,
        rate_percent: form.method === "WDV" ? form.rate_percent : null,
      });
      toast.success(`Asset ${form.code} created as a draft`);
      setForm({ code: "", name: "", company_id: "", category: "EQUIPMENT", location: "", purchase_date: "", cost: "", salvage_value: "0", method: "SLM", useful_life_years: "", rate_percent: "" });
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-asset-button"><Plus className="h-4 w-4" /> New Asset</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>New Fixed Asset</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="create-asset-form">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Code</Label><Input placeholder="Automatic if blank" value={form.code} onChange={set("code")} className="mt-1.5" data-testid="asset-code-input" /></div>
            <div><Label>Name</Label><Input required value={form.name} onChange={set("name")} className="mt-1.5" data-testid="asset-name-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <CompanyField label="Company" value={form.company_id} onChange={(v) => setForm({ ...form, company_id: v })} testId="asset-company-select" />
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1.5" data-testid="asset-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Purchase date</Label><Input required type="date" value={form.purchase_date} onChange={set("purchase_date")} className="mt-1.5" data-testid="asset-purchase-date-input" /></div>
            <div><Label>Cost</Label><Input required type="number" step="0.01" value={form.cost} onChange={set("cost")} className="mt-1.5" data-testid="asset-cost-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Salvage value</Label><Input type="number" step="0.01" value={form.salvage_value} onChange={set("salvage_value")} className="mt-1.5" data-testid="asset-salvage-input" /></div>
            <div><Label>Location</Label><Input value={form.location} onChange={set("location")} className="mt-1.5" data-testid="asset-location-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Depreciation method</Label>
              <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                <SelectTrigger className="mt-1.5" data-testid="asset-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>{METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.method === "SLM" ? (
              <div><Label>Useful life (years)</Label><Input required type="number" step="0.5" value={form.useful_life_years} onChange={set("useful_life_years")} className="mt-1.5" data-testid="asset-useful-life-input" /></div>
            ) : (
              <div><Label>Rate %</Label><Input required type="number" step="0.5" value={form.rate_percent} onChange={set("rate_percent")} className="mt-1.5" data-testid="asset-rate-percent-input" /></div>
            )}
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-asset-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ConfirmAssetDialog({ asset, onDone }) {
  const manual = !asset.source_bill_id && asset.acquisition_mode !== "PO_CAPITAL";
  const [open, setOpen] = useState(false);
  const [details, setDetails] = useState({});
  const [acq, setAcq] = useState({ acquisition_mode: "", vendor_id: "", vendor_bill_no: "", gst_amount: "0", opening_accumulated_depreciation: "0", entry_date: "" });
  const [vendors, setVendors] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDetails({
      code: asset.code, name: asset.name, category: asset.category, location: asset.location || "",
      salvage_value: String(asset.salvage_value ?? "0"), method: asset.method || "SLM",
      useful_life_years: asset.useful_life_years ? String(asset.useful_life_years) : "", rate_percent: asset.rate_percent ? String(asset.rate_percent) : "",
    });
    setAcq((a) => ({ ...a, entry_date: asset.purchase_date }));
    if (manual) api.get("/procurement/vendors").then(({ data }) => setVendors(data || [])).catch(() => {});
  }, [open, asset]);

  const setD = (f) => (e) => setDetails({ ...details, [f]: e.target.value });
  const setA = (f) => (e) => setAcq({ ...acq, [f]: e.target.value });
  const cost = Number(asset.cost || 0);
  const gst = Number(acq.gst_amount || 0);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/assets/${asset.id}`, {
        ...details, location: details.location || null, salvage_value: details.salvage_value || "0",
        useful_life_years: details.method === "SLM" ? details.useful_life_years || null : null,
        rate_percent: details.method === "WDV" ? details.rate_percent || null : null,
      });
      const body = manual ? {
        acquisition_mode: acq.acquisition_mode, vendor_id: acq.vendor_id || null, vendor_bill_no: acq.vendor_bill_no || null,
        gst_amount: acq.gst_amount || "0", opening_accumulated_depreciation: acq.opening_accumulated_depreciation || "0",
        entry_date: acq.entry_date || null,
      } : undefined;
      await api.post(`/assets/${asset.id}/confirm`, body);
      toast.success(`${details.code} confirmed — in the books and depreciating`);
      setOpen(false);
      onDone?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const summary = {
    OPENING: `Dr Fixed Assets ${inr(cost)}${Number(acq.opening_accumulated_depreciation) > 0 ? ` / Cr Accumulated Depreciation ${inr(acq.opening_accumulated_depreciation)}` : ""} / Cr Opening Balance Equity ${inr(cost - Number(acq.opening_accumulated_depreciation || 0))}`,
    BANK: `Dr Fixed Assets ${inr(cost)}${gst ? ` + GST ${inr(gst)}` : ""} / Cr Cash & Bank ${inr(cost + gst)}`,
    CREDIT: `A posted purchase bill of ${inr(cost + gst)} is created for the vendor — pay it from Vendor Payments`,
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`confirm-asset-button-${asset.code}`}><CheckCircle2 className="h-3.5 w-3.5" /> Confirm</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader><DialogTitle>Confirm asset — {inr(cost)}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Code</Label><Input required value={details.code || ""} onChange={setD("code")} className="mt-1.5" /></div>
            <div><Label>Name</Label><Input required value={details.name || ""} onChange={setD("name")} className="mt-1.5" /></div>
            <div>
              <Label>Category</Label>
              <Select value={details.category || "EQUIPMENT"} onValueChange={(v) => setDetails({ ...details, category: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{["PLANT", "VEHICLE", "EQUIPMENT", "FURNITURE", "IT", "BUILDING"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Location</Label><Input value={details.location || ""} onChange={setD("location")} className="mt-1.5" /></div>
            <div>
              <Label>Depreciation method</Label>
              <Select value={details.method || "SLM"} onValueChange={(v) => setDetails({ ...details, method: v })}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="SLM">Straight line (SLM)</SelectItem><SelectItem value="WDV">Written-down value (WDV)</SelectItem></SelectContent>
              </Select>
            </div>
            {details.method === "WDV"
              ? <div><Label>Rate % per year</Label><Input required type="number" step="0.01" min="0.01" max="100" value={details.rate_percent || ""} onChange={setD("rate_percent")} className="mt-1.5" /></div>
              : <div><Label>Useful life (years)</Label><Input required type="number" step="0.5" min="0.5" value={details.useful_life_years || ""} onChange={setD("useful_life_years")} className="mt-1.5" /></div>}
            <div><Label>Salvage value (₹)</Label><Input type="number" step="0.01" min="0" value={details.salvage_value || ""} onChange={setD("salvage_value")} className="mt-1.5" /></div>
          </div>

          {manual ? (
            <div className="space-y-3 rounded-lg border border-border p-3">
              <p className="text-sm font-medium text-foreground">How was this asset acquired?</p>
              <div className="grid gap-2">
                {[["OPENING", "Already owned before using this app (opening balance)"], ["BANK", "Paid from the bank"], ["CREDIT", "Bought on credit — the vendor is still to be paid"]].map(([k, label]) => (
                  <label key={k} className="flex items-center gap-2 text-sm">
                    <input type="radio" name={`acq-${asset.id}`} checked={acq.acquisition_mode === k} onChange={() => setAcq({ ...acq, acquisition_mode: k })} /> {label}
                  </label>
                ))}
              </div>
              {acq.acquisition_mode === "OPENING" && (
                <div><Label>Depreciation already charged before (₹)</Label><Input type="number" step="0.01" min="0" value={acq.opening_accumulated_depreciation} onChange={setA("opening_accumulated_depreciation")} className="mt-1.5" /></div>
              )}
              {["BANK", "CREDIT"].includes(acq.acquisition_mode) && (
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>GST paid (₹)</Label><Input type="number" step="0.01" min="0" value={acq.gst_amount} onChange={setA("gst_amount")} className="mt-1.5" /></div>
                  <div><Label>Date</Label><Input type="date" value={acq.entry_date || ""} onChange={setA("entry_date")} className="mt-1.5" /></div>
                </div>
              )}
              {acq.acquisition_mode === "CREDIT" && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Vendor</Label>
                    <Select value={acq.vendor_id} onValueChange={(v) => setAcq({ ...acq, vendor_id: v })}>
                      <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{vendors.map((v) => <SelectItem key={v.id} value={v.id}>{partyLabel(v)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Vendor's bill no.</Label><Input value={acq.vendor_bill_no} onChange={setA("vendor_bill_no")} className="mt-1.5" /></div>
                </div>
              )}
              {acq.acquisition_mode && <p className="rounded bg-muted/50 p-2 text-[11px] text-muted-foreground">Will post: {summary[acq.acquisition_mode]}</p>}
            </div>
          ) : (
            <p className="rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground">
              {asset.acquisition_mode === "PO_CAPITAL"
                ? "Bought on a PO line marked as an asset — it is already in Fixed Assets. Confirming only starts depreciation."
                : "Created from a purchase bill — confirming moves its cost from Purchases to Fixed Assets."}
            </p>
          )}
          <DialogFooter><Button type="submit" disabled={saving || (manual && !acq.acquisition_mode)}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Confirm asset</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
